import { supabase } from '../lib/supabase';

// What is BOOKED, next to what is queued. Same anon-key posture as
// services/queue.ts: the operator app has no auth.users row, so it calls
// booking.fn_* directly rather than the JWT edge functions the admin SPA uses.
//
// IMPORTANT, and the reason this is an RPC and not a table read: booking.slots
// and booking.booking_slots are behind RLS with a single policy for
// `authenticated` gated on fn_is_active_operator(). anon holds the SELECT GRANT
// but matches no POLICY, so a direct read returns ZERO ROWS WITH NO ERROR — the
// strip would print "niets geboekt" on a sold-out afternoon and staff would keep
// selling walk-ins. fn_calendar_departures is SECURITY DEFINER and granted to
// anon; it is the same function the booking admin calls.
//
// Contract: booking.fn_calendar_departures(p_from, p_to, p_experience_id,
// p_location_id) -> jsonb array, one row per (slot x room x experience).

/** One row as the RPC returns it. `booked`/`capacity` are ROOM-wide and repeat
 *  across the experiences sharing that room and slot; `booked_in_experience` is
 *  the per-experience share. */
export interface CalendarDeparture {
  date: string;
  slot_start: string;
  /** "12:00" in the location's own clock — use this, not a local re-format. */
  time_of_day: string;
  room_id: string;
  room_name: string;
  capacity: number;
  booked: number;
  location_id: string;
  location_name: string;
  experience_id: string | null;
  experience_name: string | null;
  booked_in_experience: number;
  bookings_count: number;
  lifecycle_summary: Record<string, number> | null;
}

/** A slot as the UI wants it: one entry per (time x room), experiences folded in. */
export interface BookingSlot {
  key: string;
  slotStart: string;
  timeOfDay: string;
  /** Every room this start time covers, so the UI can name them when a
   *  location has more than one. */
  roomNames: string[];
  /** Summed across rooms; NOT the sum of the per-experience numbers. */
  capacity: number;
  booked: number;
  /** 0..1, clamped. Drives both the bar width and its hue. */
  ratio: number;
  /** "Egypt 4 · Odyssey 2", or null when nothing is booked. */
  experiencesLabel: string | null;
}

export async function fetchCalendarDepartures(
  locationId: string,
  from: string,
  to: string,
  experienceId: string | null = null,
): Promise<CalendarDeparture[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_calendar_departures', {
      p_from: from,
      p_to: to,
      p_experience_id: experienceId,
      p_location_id: locationId,
    });
  if (error) throw error;
  return (data ?? []) as CalendarDeparture[];
}

/** Fold the RPC's (slot x room x experience) rows into ONE row per start time,
 *  summing across rooms.
 *
 *  Summing is deliberate and copied from the booking admin's SlotCell, which
 *  dedupes by room_id and then totals: a walk-in can be put in either room, so
 *  "is there space at 13:00" is a location-wide question. `capacity` and
 *  `booked` repeat across the experiences sharing a room, hence the dedupe
 *  before the sum — adding the raw rows would multiply capacity by the number
 *  of experiences the room offers. */
export function toSlots(rows: CalendarDeparture[]): BookingSlot[] {
  interface Acc {
    row: CalendarDeparture;
    rooms: Map<string, { capacity: number; booked: number; name: string }>;
    parts: Map<string, number>;
  }
  const byTime = new Map<string, Acc>();

  for (const r of rows) {
    if (!r.capacity || r.capacity <= 0) continue;
    let acc = byTime.get(r.slot_start);
    if (!acc) {
      acc = { row: r, rooms: new Map(), parts: new Map() };
      byTime.set(r.slot_start, acc);
    }
    acc.rooms.set(r.room_id, { capacity: r.capacity, booked: r.booked, name: r.room_name });
    // Only name an experience that has people in it: a slot lists every
    // experience the room offers, and "Egypt 0 · Odyssey 0" is noise.
    if (r.experience_name && r.booked_in_experience > 0) {
      acc.parts.set(r.experience_name, (acc.parts.get(r.experience_name) ?? 0) + r.booked_in_experience);
    }
  }

  const out: BookingSlot[] = [];
  for (const [slotStart, acc] of byTime) {
    let capacity = 0;
    let booked = 0;
    for (const v of acc.rooms.values()) {
      capacity += v.capacity;
      booked += v.booked;
    }
    const ratio = capacity > 0 ? Math.max(0, Math.min(1, booked / capacity)) : 0;
    const parts = [...acc.parts.entries()].map(([name, n]) => `${name} ${n}`);
    out.push({
      key: slotStart,
      slotStart,
      timeOfDay: acc.row.time_of_day,
      roomNames: [...acc.rooms.values()].map((v) => v.name).sort(),
      capacity,
      booked,
      ratio,
      experiencesLabel: parts.length > 0 ? parts.join(' · ') : null,
    });
  }
  out.sort((a, b) => (a.slotStart < b.slotStart ? -1 : a.slotStart > b.slotStart ? 1 : 0));
  return out;
}

/** Same fold, but kept split per room — the calendar's "look closer" view,
 *  where a two-room location should see each room rather than a total. */
export function toSlotsByRoom(rows: CalendarDeparture[]): Map<string, BookingSlot[]> {
  const byRoom = new Map<string, CalendarDeparture[]>();
  for (const r of rows) {
    const list = byRoom.get(r.room_name);
    if (list) list.push(r); else byRoom.set(r.room_name, [r]);
  }
  const out = new Map<string, BookingSlot[]>();
  for (const [room, list] of [...byRoom.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    out.set(room, toSlots(list));
  }
  return out;
}

/** The fill colour for a capacity bar.
 *
 *  Deliberately the SAME ramp the booking admin uses
 *  (RealityBooking/admin/src/components/SlotCell.tsx): hue = 130 - 130 * ratio,
 *  green when empty, yellow at half, red when full. Staff move between the two
 *  screens, so a sold-out slot has to look sold out in both. Only the lightness
 *  differs, because the operator is a dark theme. */
export function capacityHue(ratio: number): number {
  const r = Math.max(0, Math.min(1, ratio));
  return 130 - 130 * r;
}

export function capacityColor(ratio: number): string {
  // Same HUE ramp as the booking admin, so a sold-out slot still reads sold out
  // in both products, but pulled down to the saturation the house style lives
  // at. The admin is a light theme where 60% sings; on the operator's near-black
  // ground beside #d6b25e gold it screamed, which is half of why the first pass
  // did not look like Neoseum (Dirk 2026-09-15).
  return `hsl(${capacityHue(ratio)} 34% 44%)`;
}

/** Today in the location's clock, as the RPC wants it (YYYY-MM-DD). */
export function amsterdamDate(d: Date = new Date()): string {
  // en-CA gives ISO order; the timeZone does the actual work.
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
}
